import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X, User, LogOut, Trophy, Users, Target, Calendar, Shield, Trash2 } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const { currentUser, userRole, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen)

  // Define menu items based on user role
  let menuItems = []

  if (userRole === 'scorekeeper') {
    // Score Keepers only have access to Fixtures and Results
    menuItems = [
      { path: '/fixtures', label: 'Fixtures', icon: Calendar },
      { path: '/results', label: 'Results', icon: Trophy }
    ]
  } else {
    // Admins and Super Admins have access to all menus
    menuItems = [
      { path: '/dashboard', label: 'Dashboard', icon: Trophy },
      { path: '/teams', label: 'Teams', icon: Shield },
      { path: '/players', label: 'Players', icon: Users },
      { path: '/league', label: 'League', icon: Target },
      { path: '/fixtures', label: 'Fixtures', icon: Calendar },
      { path: '/results', label: 'Results', icon: Trophy }
    ]

    // Only Super Admins can access User Management and Reset Data
    if (userRole === 'super-admin') {
      menuItems.push({ path: '/admin/users', label: 'User Management', icon: User })
      menuItems.push({ path: '/admin/reset', label: 'Reset Data', icon: Trash2 })
    }
  }

  const isActive = (path) => location.pathname === path

  return (
    <>
      <style>{`
        @media (max-width: 1024px) {
          .desktop-menu, .desktop-user-menu {
            display: none !important;
          }
          .mobile-menu-button {
            display: flex !important;
          }
        }
        @media (min-width: 1025px) {
          .desktop-menu, .desktop-user-menu {
            display: flex !important;
          }
          .mobile-menu-button {
            display: none !important;
          }
        }
        .mobile-menu-button {
          display: none;
        }
        
        /* Fix navbar positioning and z-index */
        nav {
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 1000 !important;
          width: 100% !important;
        }
        
        /* Ensure main content accounts for navbar */
        main, .main-content {
          margin-top: 70px !important;
          min-height: calc(100vh - 70px) !important;
        }
        
        /* Fix any layout issues */
        body {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}</style>
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'white',
          backdropFilter: isScrolled ? 'blur(10px)' : 'none',
          borderBottom: '1px solid #E5E7EB',
          boxShadow: isScrolled ? '0 4px 20px rgba(0, 0, 0, 0.1)' : '0 1px 3px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease'
        }}
      >
      <div style={{ 
        maxWidth: '100%', 
        margin: '0 auto', 
        padding: '0 clamp(1rem, 3vw, 2rem)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          height: '70px',
          gap: 'clamp(0.5rem, 2vw, 1rem)'
        }}>
          {/* Logo */}
          <Link 
            to="/dashboard" 
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              textDecoration: 'none',
              color: '#1C563D',
              fontWeight: '700',
              fontSize: '1.5rem',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            <img 
              src="/offcourt_logo.png" 
              alt="OffCourt Official League" 
              style={{ 
                width: 'clamp(35px, 5vw, 40px)', 
                height: 'clamp(35px, 5vw, 40px)',
                objectFit: 'contain',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
              }} 
            />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <span style={{ fontSize: 'clamp(1rem, 2vw, 1.25rem)', fontWeight: 'bold', lineHeight: 1 }}>OffCourt</span>
              <span style={{ fontSize: 'clamp(0.625rem, 1.2vw, 0.75rem)', fontWeight: '500', color: '#6B7280', lineHeight: 1 }}>Official League</span>
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="desktop-menu" style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(0.5rem, 2vw, 2rem)',
            flex: 1,
            justifyContent: 'center'
          }}>
            {menuItems.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem clamp(0.5rem, 1.5vw, 1rem)',
                    borderRadius: '0.5rem',
                    textDecoration: 'none',
                    color: isActive(item.path) ? '#1C563D' : '#6B7280',
                    background: isActive(item.path) ? '#F0FDF4' : 'transparent',
                    fontWeight: isActive(item.path) ? '600' : '500',
                    fontSize: 'clamp(0.875rem, 1.5vw, 1rem)',
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.background = '#F9FAFB';
                      e.currentTarget.style.color = '#1C563D';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#6B7280';
                    }
                  }}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                </Link>
              )
            })}
          </div>

          {/* User Menu - Desktop */}
          {currentUser && (
            <div className="desktop-user-menu" style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(0.5rem, 1.5vw, 1rem)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem clamp(0.75rem, 1.5vw, 1rem)',
                background: '#F0FDF4',
                borderRadius: '0.5rem',
                border: '1px solid #D1FAE5'
              }}>
                <User size={18} style={{ color: '#065F46' }} />
                <span style={{ 
                  fontSize: 'clamp(0.75rem, 1.3vw, 0.875rem)',
                  fontWeight: '600',
                  color: '#065F46'
                }}>
                  {currentUser.displayName}
                </span>
                <span style={{ 
                  fontSize: 'clamp(0.625rem, 1.1vw, 0.75rem)',
                  color: '#6B7280',
                  background: 'white',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '0.25rem'
                }}>
                  {userRole}
                </span>
              </div>
              <button
                onClick={handleLogout}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem clamp(0.75rem, 1.5vw, 1rem)',
                  background: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: 'clamp(0.75rem, 1.3vw, 0.875rem)',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#DC2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#EF4444';
                }}
              >
                <LogOut size={16} />
                Logout
              </button>
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '44px',
              height: '44px',
              background: '#1C563D',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            className="mobile-menu-button"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mobile-menu"
              style={{
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px)',
                borderTop: '1px solid rgba(28, 86, 61, 0.1)',
                borderRadius: '0 0 1rem 1rem',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div style={{ padding: '1.5rem 0' }}>
                {/* Mobile Logo */}
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.75rem', 
                  padding: '1rem 1.5rem',
                  marginBottom: '1rem',
                  background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                  borderRadius: '0.5rem',
                  border: '1px solid #10B981'
                }}>
                  <img 
                    src="/offcourt_logo.png" 
                    alt="OffCourt Official League" 
                    style={{ 
                      width: '32px', 
                      height: '32px',
                      objectFit: 'contain'
                    }} 
                  />
                  <div>
                    <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#065F46' }}>OffCourt</div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>Official League</div>
                  </div>
                </div>
                
                {menuItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '1rem 1.5rem',
                        textDecoration: 'none',
                        color: isActive(item.path) ? '#1C563D' : '#6B7280',
                        background: isActive(item.path) 
                          ? 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)' 
                          : 'transparent',
                        fontWeight: isActive(item.path) ? '600' : '500',
                        transition: 'all 0.2s ease',
                        borderLeft: isActive(item.path) ? '4px solid #10B981' : '4px solid transparent'
                      }}
                    >
                      <Icon size={20} />
                      <span>{item.label}</span>
                    </Link>
                  )
                })}
                
                {currentUser && (
                  <div style={{ 
                    padding: '1.5rem',
                    borderTop: '1px solid rgba(28, 86, 61, 0.1)',
                    marginTop: '1rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      marginBottom: '1rem',
                      padding: '0.75rem',
                      background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                      borderRadius: '0.5rem',
                      border: '1px solid #10B981'
                    }}>
                      <User size={20} style={{ color: '#065F46' }} />
                      <div>
                        <div style={{ 
                          fontWeight: '600',
                          color: '#065F46',
                          fontSize: '0.875rem'
                        }}>
                          {currentUser.displayName}
                        </div>
                        <div style={{ 
                          fontSize: '0.75rem',
                          color: '#6B7280'
                        }}>
                          {userRole}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        handleLogout()
                        setIsMenuOpen(false)
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        width: '100%',
                        padding: '0.75rem',
                        background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.5rem',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
                      }}
                    >
                      <LogOut size={18} />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      </nav>
    </>
  )
}

export default Navbar